import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { getUrlById, createUrl, updateUrl } from '../../services/urlsService';
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";

// Make sure this array includes all your options
const urlTypes = [
  { value: 'landbot', label: 'Landbot' },
  { value: 'web', label: 'Web' },
  { value: 'hdi', label: 'HDI' }, 
  { value: 'api', label: 'API' },
  { value: 'other', label: 'Otro' }
];

export default function UrlsForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      type: 'landbot',
      customDatacodproducto: '',
      url: ''
    }
  });

  // Check how the URL parameter is being accessed
  const { id } = useParams();
  
  // Check the data loading logic
  useEffect(() => {
    if (id) {
      const loadUrl = async () => {
        try {
          setLoading(true);
          const urlData = await getUrlById(id); // Changed variable name from data to urlData
          // Reset form with the loaded data
          reset({
            name: urlData.name || '',
            type: urlData.type || 'landbot',
            customDatacodproducto: urlData.customDatacodproducto || '',
            url: urlData.url || ''
          });
        } catch (err) {
          console.error('Error al cargar la URL:', err);
          setError('Error al cargar la URL. Por favor, intente nuevamente.');
        } finally {
          setLoading(false);
        }
      };
      
      loadUrl();
    }
  }, [id, reset]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);
      
      // Asegurarse de que la URL tenga un UUID si es nueva
      if (!id) {
        data.uuid = uuidv4();
      }
      
      if (id) {
        // Actualizar URL existente
        await updateUrl(id, {
          name: data.name,
          type: data.type,
          customDatacodproducto: data.customDatacodproducto,
          url: data.url
        });
      } else {
        // Crear nueva URL
        await createUrl({
          name: data.name,
          type: data.type,
          customDatacodproducto: data.customDatacodproducto,
          url: data.url,
          uuid: data.uuid
        });
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/admin/urls');
      }, 2000);
    } catch (err) {
      console.error('Error al guardar la URL:', err);
      setError('Error al guardar la URL. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Modify the Select component to ensure it renders all options
  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">{id ? 'Editar' : 'Nueva'} URL</h1>
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {success && (
        <Alert>
          <AlertDescription>
            {id ? 'URL actualizada correctamente' : 'URL creada correctamente'}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                {...register('name', { required: 'El nombre es obligatorio' })}
                placeholder="Nombre corto de la URL"
                className="bg-white"
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Tipo</Label>
              <Select 
                onValueChange={value => setValue('type', value)}
                defaultValue={watch('type')}
                className="bg-white"
              >
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Seleccione tipo" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-300">
                  {urlTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="customDatacodproducto">Código de Producto</Label>
              <Input
                id="customDatacodproducto"
                {...register('customDatacodproducto')}
                placeholder="Ej: HDI-2826"
                className="bg-white"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">URL</Label>
              <Input
                id="url"
                {...register('url', { required: 'La URL es obligatoria' })}
                placeholder="https://ejemplo.com"
                className="bg-white"
              />
              {errors.url && (
                <p className="text-sm text-destructive">{errors.url.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/urls')}
            className="border-gray-300 text-gray-500 hover:bg-gray-50 focus:ring-gray-200"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            {loading ? 'Guardando...' : id ? 'Actualizar URL' : 'Crear URL'}
          </Button>
        </div>
      </form>
    </div>
  );
}